import { useState } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  getPrompt,
  updatePrompt,
  createPromptVersion,
  setCurrentPromptVersion,
  deletePrompt,
  PromptUpdate,
  PromptVersionCreate,
  PromptVersion,
  UseCase,
} from '../services/api'

const USE_CASE_OPTIONS: { value: UseCase; label: string }[] = [
  { value: 'aidw', label: 'AIDW' },
  { value: 'caf', label: 'CAF' },
  { value: 'waf', label: 'WAF' },
  { value: 'other', label: 'Other' },
]

const CATEGORY_OPTIONS = [
  { value: 'executive_summary', label: 'Executive Summary' },
  { value: 'migration_strategy', label: 'Migration Strategy' },
  { value: 'architecture', label: 'Architecture' },
  { value: 'security', label: 'Security' },
  { value: 'governance', label: 'Governance' },
  { value: 'operations', label: 'Operations' },
  { value: 'other', label: 'Other' },
]

export default function PromptDetail() {
  const { promptId } = useParams<{ promptId: string }>()
  const navigate = useNavigate()
  const queryClient = useQueryClient()

  const [isEditingMeta, setIsEditingMeta] = useState(false)
  const [showNewVersionModal, setShowNewVersionModal] = useState(false)
  const [selectedVersion, setSelectedVersion] = useState<PromptVersion | null>(null)
  const [metaForm, setMetaForm] = useState<PromptUpdate>({})
  const [versionForm, setVersionForm] = useState<PromptVersionCreate>({ content: '', change_notes: '' })

  const { data: prompt, isLoading, error } = useQuery({
    queryKey: ['prompt', promptId],
    queryFn: async () => {
      const response = await getPrompt(promptId!)
      return response.data
    },
    enabled: !!promptId,
  })

  const updateMutation = useMutation({
    mutationFn: (data: PromptUpdate) => updatePrompt(promptId!, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['prompt', promptId] })
      setIsEditingMeta(false)
    },
  })

  const createVersionMutation = useMutation({
    mutationFn: (data: PromptVersionCreate) => createPromptVersion(promptId!, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['prompt', promptId] })
      setShowNewVersionModal(false)
      setVersionForm({ content: '', change_notes: '' })
    },
  })

  const setCurrentMutation = useMutation({
    mutationFn: (versionId: string) => setCurrentPromptVersion(promptId!, versionId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['prompt', promptId] })
    },
  })

  const deleteMutation = useMutation({
    mutationFn: () => deletePrompt(promptId!),
    onSuccess: () => {
      navigate('/prompts')
    },
  })

  const handleMetaSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    updateMutation.mutate(metaForm)
  }

  const handleVersionSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    createVersionMutation.mutate(versionForm)
  }

  const startEditMeta = () => {
    if (prompt) {
      setMetaForm({
        display_name: prompt.display_name,
        description: prompt.description,
        category: prompt.category,
        use_case: prompt.use_case,
        is_active: prompt.is_active,
      })
      setIsEditingMeta(true)
    }
  }

  const startNewVersion = () => {
    if (prompt?.current_version) {
      setVersionForm({
        content: prompt.current_version.content,
        change_notes: '',
      })
    }
    setShowNewVersionModal(true)
  }

  const cardStyle = {
    background: 'rgba(15, 23, 42, 0.6)',
    backdropFilter: 'blur(20px)',
    border: '1px solid rgba(100, 180, 255, 0.1)',
    borderRadius: '16px',
  }

  const inputStyle = {
    background: 'rgba(30, 41, 59, 0.5)',
    border: '1px solid rgba(100, 180, 255, 0.15)',
    color: '#e2e8f0',
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
      </div>
    )
  }

  if (error || !prompt) {
    return (
      <div className="text-center py-16 rounded-xl" style={cardStyle}>
        <i className="fa-solid fa-exclamation-circle text-4xl mb-4" style={{ color: '#ef4444' }}></i>
        <p style={{ color: '#ef4444' }}>Prompt not found</p>
        <Link to="/prompts" className="text-blue-400 hover:underline mt-2 inline-block">
          Back to Prompts
        </Link>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <Link
            to="/prompts"
            className="p-2 rounded-lg hover:bg-white/10 transition-colors"
            style={{ color: '#94a3b8' }}
          >
            <i className="fa-solid fa-arrow-left"></i>
          </Link>
          <div>
            <h1 className="text-2xl font-semibold" style={{ color: '#f1f5f9' }}>
              {prompt.display_name}
            </h1>
            <p className="text-sm" style={{ color: 'rgba(148, 163, 184, 0.6)' }}>
              {prompt.name}
            </p>
          </div>
        </div>
        <div className="flex items-center space-x-2">
          <button
            onClick={startNewVersion}
            className="flex items-center space-x-2 px-4 py-2 rounded-lg font-medium transition-all hover:scale-105"
            style={{
              background: 'linear-gradient(135deg, rgba(34, 197, 94, 0.8) 0%, rgba(16, 185, 129, 0.8) 100%)',
              color: 'white',
            }}
          >
            <i className="fa-solid fa-code-branch"></i>
            <span>New Version</span>
          </button>
          <button
            onClick={() => {
              if (confirm('Delete this prompt and all its versions?')) {
                deleteMutation.mutate()
              }
            }}
            className="p-2 rounded-lg hover:bg-white/10 transition-colors"
            style={{ color: '#ef4444' }}
            title="Delete Prompt"
          >
            <i className="fa-solid fa-trash"></i>
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left Column - Metadata */}
        <div className="space-y-6">
          {/* Metadata Card */}
          <div className="p-5 rounded-xl" style={cardStyle}>
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-medium" style={{ color: '#f1f5f9' }}>Details</h2>
              {!isEditingMeta && (
                <button
                  onClick={startEditMeta}
                  className="text-sm px-3 py-1 rounded-lg hover:bg-white/10 transition-colors"
                  style={{ color: '#60a5fa' }}
                >
                  <i className="fa-solid fa-edit mr-1"></i>
                  Edit
                </button>
              )}
            </div>

            {isEditingMeta ? (
              <form onSubmit={handleMetaSubmit} className="space-y-3">
                <div>
                  <label className="block text-xs font-medium mb-1" style={{ color: '#94a3b8' }}>
                    Display Name
                  </label>
                  <input
                    type="text"
                    value={metaForm.display_name || ''}
                    onChange={(e) => setMetaForm({ ...metaForm, display_name: e.target.value })}
                    className="w-full px-3 py-2 rounded-lg text-sm"
                    style={inputStyle}
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium mb-1" style={{ color: '#94a3b8' }}>
                    Description
                  </label>
                  <textarea
                    value={metaForm.description || ''}
                    onChange={(e) => setMetaForm({ ...metaForm, description: e.target.value })}
                    rows={3}
                    className="w-full px-3 py-2 rounded-lg text-sm"
                    style={inputStyle}
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium mb-1" style={{ color: '#94a3b8' }}>
                    Category
                  </label>
                  <select
                    value={metaForm.category || ''}
                    onChange={(e) => setMetaForm({ ...metaForm, category: e.target.value })}
                    className="w-full px-3 py-2 rounded-lg text-sm"
                    style={inputStyle}
                  >
                    {CATEGORY_OPTIONS.map(opt => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium mb-1" style={{ color: '#94a3b8' }}>
                    Use Case
                  </label>
                  <select
                    value={metaForm.use_case || ''}
                    onChange={(e) => setMetaForm({ ...metaForm, use_case: e.target.value as UseCase })}
                    className="w-full px-3 py-2 rounded-lg text-sm"
                    style={inputStyle}
                  >
                    {USE_CASE_OPTIONS.map(opt => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                </div>
                <div className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    id="is_active"
                    checked={metaForm.is_active ?? true}
                    onChange={(e) => setMetaForm({ ...metaForm, is_active: e.target.checked })}
                  />
                  <label htmlFor="is_active" className="text-sm" style={{ color: '#94a3b8' }}>
                    Active
                  </label>
                </div>
                <div className="flex justify-end space-x-2 pt-2">
                  <button
                    type="button"
                    onClick={() => setIsEditingMeta(false)}
                    className="px-3 py-1.5 rounded-lg text-sm"
                    style={{ color: '#94a3b8', background: 'rgba(100, 116, 139, 0.2)' }}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={updateMutation.isPending}
                    className="px-3 py-1.5 rounded-lg text-sm"
                    style={{ background: 'rgba(59, 130, 246, 0.8)', color: 'white' }}
                  >
                    {updateMutation.isPending ? 'Saving...' : 'Save'}
                  </button>
                </div>
              </form>
            ) : (
              <div className="space-y-3 text-sm">
                <div>
                  <span style={{ color: 'rgba(148, 163, 184, 0.6)' }}>Description</span>
                  <p style={{ color: '#e2e8f0' }}>{prompt.description || '-'}</p>
                </div>
                <div className="flex justify-between">
                  <span style={{ color: 'rgba(148, 163, 184, 0.6)' }}>Category</span>
                  <span style={{ color: '#e2e8f0' }}>{prompt.category}</span>
                </div>
                <div className="flex justify-between">
                  <span style={{ color: 'rgba(148, 163, 184, 0.6)' }}>Use Case</span>
                  <span style={{ color: '#e2e8f0' }}>{prompt.use_case.toUpperCase()}</span>
                </div>
                <div className="flex justify-between">
                  <span style={{ color: 'rgba(148, 163, 184, 0.6)' }}>Status</span>
                  <span style={{ color: prompt.is_active ? '#22c55e' : '#ef4444' }}>
                    {prompt.is_active ? 'Active' : 'Inactive'}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span style={{ color: 'rgba(148, 163, 184, 0.6)' }}>Versions</span>
                  <span style={{ color: '#e2e8f0' }}>{prompt.versions.length}</span>
                </div>
              </div>
            )}
          </div>

          {/* Version History */}
          <div className="p-5 rounded-xl" style={cardStyle}>
            <h2 className="text-lg font-medium mb-4" style={{ color: '#f1f5f9' }}>
              Version History
            </h2>
            <div className="space-y-2 max-h-[400px] overflow-y-auto">
              {prompt.versions.map((version) => (
                <button
                  key={version.id}
                  onClick={() => setSelectedVersion(version)}
                  className={`w-full text-left p-3 rounded-lg transition-all ${
                    selectedVersion?.id === version.id ? 'ring-2 ring-blue-500' : ''
                  }`}
                  style={{
                    background: selectedVersion?.id === version.id
                      ? 'rgba(59, 130, 246, 0.2)'
                      : 'rgba(30, 41, 59, 0.5)',
                  }}
                >
                  <div className="flex items-center justify-between mb-1">
                    <span className="font-medium" style={{ color: '#f1f5f9' }}>
                      v{version.version_number}
                    </span>
                    {version.is_current && (
                      <span
                        className="px-2 py-0.5 rounded text-xs"
                        style={{ background: 'rgba(34, 197, 94, 0.2)', color: '#22c55e' }}
                      >
                        Current
                      </span>
                    )}
                  </div>
                  {version.change_notes && (
                    <p className="text-xs truncate" style={{ color: 'rgba(148, 163, 184, 0.6)' }}>
                      {version.change_notes}
                    </p>
                  )}
                  <p className="text-xs mt-1" style={{ color: 'rgba(148, 163, 184, 0.5)' }}>
                    {new Date(version.created_at).toLocaleDateString()}
                    {version.created_by && ` by ${version.created_by}`}
                  </p>
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Right Column - Content */}
        <div className="lg:col-span-2">
          <div className="p-5 rounded-xl h-full" style={cardStyle}>
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-medium" style={{ color: '#f1f5f9' }}>
                {selectedVersion
                  ? `Version ${selectedVersion.version_number}`
                  : prompt.current_version
                  ? `Current Version (v${prompt.current_version.version_number})`
                  : 'No Versions'}
              </h2>
              {selectedVersion && !selectedVersion.is_current && (
                <button
                  onClick={() => setCurrentMutation.mutate(selectedVersion.id)}
                  disabled={setCurrentMutation.isPending}
                  className="text-sm px-3 py-1.5 rounded-lg transition-colors"
                  style={{ background: 'rgba(34, 197, 94, 0.2)', color: '#22c55e' }}
                >
                  {setCurrentMutation.isPending ? 'Setting...' : 'Set as Current'}
                </button>
              )}
            </div>

            {selectedVersion?.change_notes && (
              <div className="mb-4 p-3 rounded-lg" style={{ background: 'rgba(59, 130, 246, 0.1)' }}>
                <span className="text-xs font-medium" style={{ color: '#60a5fa' }}>Change Notes:</span>
                <p className="text-sm mt-1" style={{ color: '#e2e8f0' }}>{selectedVersion.change_notes}</p>
              </div>
            )}

            <pre
              className="p-4 rounded-lg text-sm font-mono whitespace-pre-wrap overflow-auto"
              style={{
                ...inputStyle,
                maxHeight: 'calc(100vh - 400px)',
                minHeight: '300px',
              }}
            >
              {selectedVersion?.content || prompt.current_version?.content || 'No content available'}
            </pre>
          </div>
        </div>
      </div>

      {/* New Version Modal */}
      {showNewVersionModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: 'rgba(0, 0, 0, 0.7)', backdropFilter: 'blur(4px)' }}>
          <div className="w-full max-w-3xl max-h-[90vh] overflow-y-auto rounded-2xl p-6" style={cardStyle}>
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-semibold" style={{ color: '#f1f5f9' }}>Create New Version</h2>
              <button onClick={() => setShowNewVersionModal(false)} style={{ color: '#94a3b8' }}>
                <i className="fa-solid fa-times text-xl"></i>
              </button>
            </div>

            <form onSubmit={handleVersionSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1" style={{ color: '#94a3b8' }}>
                  Change Notes
                </label>
                <input
                  type="text"
                  value={versionForm.change_notes || ''}
                  onChange={(e) => setVersionForm({ ...versionForm, change_notes: e.target.value })}
                  placeholder="What changed in this version?"
                  className="w-full px-3 py-2 rounded-lg text-sm"
                  style={inputStyle}
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-1" style={{ color: '#94a3b8' }}>
                  Prompt Content
                </label>
                <textarea
                  required
                  value={versionForm.content}
                  onChange={(e) => setVersionForm({ ...versionForm, content: e.target.value })}
                  rows={16}
                  className="w-full px-3 py-2 rounded-lg text-sm font-mono"
                  style={inputStyle}
                />
              </div>

              <div className="flex justify-end space-x-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowNewVersionModal(false)}
                  className="px-4 py-2 rounded-lg text-sm font-medium"
                  style={{ color: '#94a3b8', background: 'rgba(100, 116, 139, 0.2)' }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={createVersionMutation.isPending}
                  className="px-4 py-2 rounded-lg text-sm font-medium"
                  style={{
                    background: 'linear-gradient(135deg, rgba(34, 197, 94, 0.8) 0%, rgba(16, 185, 129, 0.8) 100%)',
                    color: 'white',
                  }}
                >
                  {createVersionMutation.isPending ? 'Creating...' : 'Create Version'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  )
}
